import { FireButtonVariant } from '../../core-field/fire-field-props'

/**
 * Props for the presentational pieces `ui2:apiField` and `ui2:eventField` share. The two fields own
 * their own request logic and failure classification; everything below is pure rendering, so it has
 * one implementation per platform instead of two near-copies.
 */

export interface FireTriggerButtonProps {
  label?: string
  /** Appearance. Already resolved against the per-button default by the field. */
  variant?: FireButtonVariant
  isDisabled?: boolean
  isLoading?: boolean
  onPress: () => void
}

export interface FireRetryButtonProps {
  label?: string
  variant?: FireButtonVariant
  /** Blocks a second press while a retry is in flight, and swaps the label for a spinner. */
  isLoading?: boolean
  isDisabled?: boolean
  onPress: () => void
}

/**
 * Inline "working on it" text beside a spinner. Deliberately not a banner — a request in flight is
 * a transient state, and a full banner made every fire look like an outcome.
 */
export interface FireLoadingIndicatorProps {
  /**
   * Shown in place of `message` once the request has been slow enough to warrant reassurance. The
   * field owns the timer; this only decides which string renders.
   */
  isSlow?: boolean
  message?: string
}

/**
 * The lookup input for `fireMode: 'inlineLookup'` — a search-style control with a round submit
 * button, mirroring `ui2:entitySearchField`'s submit mode. The component owns the typed text, the
 * button's appearance timing and the committed/uncommitted treatment; the field only reacts to
 * `onSubmit`.
 */
export interface FireInlineLookupInputProps {
  placeholder?: string
  /** Submit stays disabled below this length. Default 1. */
  minQueryLength?: number
  isDisabled?: boolean
  isLoading?: boolean
  /**
   * Disables submit while leaving the typed text visible — used once the attempt limit is reached,
   * where inlineLookup reuses this button as its retry affordance rather than rendering one.
   */
  isSubmitDisabled?: boolean
  /** Fired with the trimmed query when the user submits. */
  onSubmit: (query: string) => void
  /** Fired on every keystroke, with the raw text. */
  onQueryChange?: (query: string) => void
}

/** One labeled line in the debug panel. `value` is rendered verbatim. */
export interface FireDebugRow {
  label: string
  value: string
}

export interface FireDebugPanelProps {
  /** Panel heading, e.g. `[apiField debug]`. */
  title: string
  isLoading?: boolean
  rows: FireDebugRow[]
  /** Pretty-printed blocks rendered below the rows, in order. */
  blocks?: Array<{ label: string, content: string }>
}

/** Copy shared by both fields, so a wording change lands in one place. */
export const FIRE_SLOW_LOADING_THRESHOLD_MS = 5000
export const FIRE_SLOW_LOADING_MESSAGE =
  'This is taking longer than expected, one moment please.'
export const FIRE_DEFAULT_MAX_ATTEMPTS_MESSAGE = 'Maximum number of attempts reached.'
export const FIRE_DEFAULT_TIMEOUT_TITLE = 'Request timed out'
export const FIRE_DEFAULT_NO_INTERNET_TITLE = 'No internet connection'
export const FIRE_DEFAULT_NO_INTERNET_MESSAGE =
  'Please check your internet connection and try again.'
export const FIRE_DEFAULT_GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'
export const FIRE_DEFAULT_DEBOUNCE_MS = 500

/** Delay before the inline-lookup submit button animates in, matching entitySearchField. */
export const FIRE_LOOKUP_BUTTON_APPEAR_DELAY_MS = 400
export const FIRE_DEFAULT_LOOKUP_PLACEHOLDER = 'Search...'

/** Whether an inline-lookup query is long enough to submit. */
export const isFireLookupQueryValid = (query: string, minQueryLength = 1): boolean => {
  const trimmed = (query ?? '').trim()
  return trimmed.length > 0 && trimmed.length >= minQueryLength
}

/**
 * Builds the scope a `lazySuccessUiSchema` / `lazyFailureUiSchema` snippet is rendered against.
 *
 * These snippets go through `ViewRenderer`, whose root scope is built from its `state` prop alone
 * (`AbstractRenderer.makeRootScope`) — it does **not** inherit the parent form's frames. So a
 * snippet handed only the response could not read anything off the form, including a path the
 * field's own `valueMappings` had just written to; the expression silently resolved to undefined.
 *
 * `frames.getStates()` flattens every scope the parent can see into one plain object, which is the
 * shape `ViewRenderer` wants. The response is spread last so `apiResponse` / `eventResponse` win
 * over any form key of the same name.
 *
 * This is a render-time snapshot, which is enough: the snippet re-renders with the field, and the
 * field re-renders when the entity changes.
 */
export function buildFireLazyScope(frames: any, responseScope: any): Record<string, unknown> {
  let formScope: Record<string, unknown> = {}
  try {
    formScope = frames?.getStates?.() ?? {}
  } catch (e) {
    // A scope that can't be read is not worth failing the render over — the response still resolves.
    formScope = {}
  }
  return { ...formScope, ...responseScope }
}

/**
 * Serializes a value for the debug panel, tolerating cycles and unserializable input — a debug view
 * that throws is worse than one that prints a placeholder.
 */
export function safeStringify(value: unknown): string {
  const seen = new WeakSet<object>()
  try {
    return JSON.stringify(value, (_k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]'
        seen.add(v)
      }
      return v
    }, 2)
  } catch (e: any) {
    return `<unserializable: ${e?.message || 'error'}>`
  }
}
